import { createDateFormatter } from "@nexus/ui";
import { useContext, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  afterDataThrough,
  categoryNoDataAreas,
  CHART_LEGEND_ICONS,
  escapeHtml,
  missingDataMode,
  NO_DATA_LABEL,
  periodBounds,
  periodDays,
  positionChartTooltip,
  trimMissing,
  useChartLifecycle,
  useChartAnimation,
  type MissingDataMode,
} from "@nexus/widget-kit";
import {
  defineLabels,
  firstError,
  WidgetIssueState,
  MetricStat,
  resolveChartColors,
  resolveLabels,
  unwrap,
  withAlpha,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetSkeleton,
  WidgetState,
  UpdatingPill,
} from "@nexus/widget-sdk";
import type { ChartColors, WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { CDNSA_DAILY_TREND_CONTRACT, type CdnsaDailyTrendData } from "./types";

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  CanvasRenderer,
]);

export interface CdnsaDailyTrendConfig {
  title?: string;
  /** `true` draws the chart in; off by default (drawn at once). */
  animateChart?: boolean;
  showLegend?: boolean;
  showNewAccounts?: boolean;
  labels?: Record<string, string>;
  /** Optional summary band above the chart; hidden by default. */
  summary?: "shown" | "hidden";
  /** Show gap (default) keeps every day of the period; Fit to data crops the days without data at its ends. */
  missingData?: MissingDataMode;
}
const SERIES_LABELS = defineLabels([
  { key: "theft", name: "Theft series", default: "Theft" },
  { key: "leeching", name: "Leeching series", default: "Leeching" },
  { key: "newAccounts", name: "New accounts series", default: "New accounts" },
]);

export const manifest: WidgetManifest = {
  id: "cdnsa-daily-trend",
  name: "Daily observed detections",
  version: "1.0.0",
  description: "Daily distinct observed CDN/SA detections, in UTC.",
  labels: SERIES_LABELS,
  dataRequests: {
    series: {
      source: "cdnsa-daily-trend",
      contract: CDNSA_DAILY_TREND_CONTRACT,
      configurable: true,
      sources: ["cdnsa-daily-trend"],
    },
  },
};
export const defaultConfig: CdnsaDailyTrendConfig = {
  title: "Daily observed detections",
  showLegend: true,
  showNewAccounts: false,
  summary: "hidden",
};

export const TYPES = ["theft_for_restreaming", "cdn_leeching"] as const;
export const TYPE_LABELS = ["Theft for restreaming", "CDN leeching"] as const;

type Point = CdnsaDailyTrendData["points"][number];

const number = new Intl.NumberFormat("en");
const average = new Intl.NumberFormat("en", { maximumFractionDigits: 0 });
const dayLabel = createDateFormatter("utc", { month: "short", day: "numeric" }, "en");
const dayLong = createDateFormatter(
  "utc",
  {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  },
  "en",
);
const fontBase = { fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif" };

const toDate = (day: string) => new Date(`${day}T00:00:00Z`);
const totalOf = (point: Point) => point.theft_for_restreaming + point.cdn_leeching;

export interface DailySlot {
  day: string;
  /** `null` for a day without data: not measured yet, or absent from the source. */
  point: Point | null;
}

/**
 * The days the chart shows. The source counts zero for a day it has not
 * measured yet, so a day starting at or after `dataThrough` is missing data.
 */
export function dailySlots(data: CdnsaDailyTrendData, mode: MissingDataMode): DailySlot[] {
  const byDay = new Map(data.points.map((point) => [point.day, point]));
  const period = periodBounds(data.period);
  const days = [...new Set([...(period ? periodDays(period) : []), ...byDay.keys()])].sort();
  const slots = days.map((day) => ({
    day,
    point: afterDataThrough(toDate(day).getTime(), data.freshness?.dataThrough)
      ? null
      : (byDay.get(day) ?? null),
  }));
  return mode === "fit" ? trimMissing(slots, (slot) => slot.point === null) : slots;
}

export interface DailySummary {
  latest: { day: string; total: number } | null;
  peak: { day: string; total: number } | null;
  /** Mean of the daily totals, over the days shown. */
  dailyAverage: number | null;
  /** Share of restreaming theft among the daily observations, `null` without any. */
  theftShare: number | null;
}

/**
 * What the band above the chart states. Daily counts are distinct per day, so
 * they are never added up into a period total (an infringement seen on two days
 * would count twice): the band gives the latest day, the peak day, the daily
 * average and the split between the two infringement types.
 */
export function summarizeDaily(points: readonly Point[]): DailySummary {
  if (!points.length) return { latest: null, peak: null, dailyAverage: null, theftShare: null };
  const sorted = [...points].sort((left, right) => left.day.localeCompare(right.day));
  const last = sorted[sorted.length - 1];
  const peak = sorted.reduce((best, point) => (totalOf(point) > totalOf(best) ? point : best));
  const totals = sorted.map(totalOf);
  const sum = totals.reduce((acc, value) => acc + value, 0);
  const theft = sorted.reduce((acc, point) => acc + point.theft_for_restreaming, 0);
  return {
    latest: { day: last.day, total: totalOf(last) },
    peak: { day: peak.day, total: totalOf(peak) },
    dailyAverage: sum / sorted.length,
    theftShare: sum > 0 ? theft / sum : null,
  };
}

export function buildDailyOption(
  data: CdnsaDailyTrendData,
  colors: ChartColors,
  options: {
    showLegend: boolean;
    showNewAccounts?: boolean;
    labels?: Record<string, string>;
    motion: boolean;
    missingData?: MissingDataMode;
  },
) {
  const slots = dailySlots(data, options.missingData ?? "gap");
  const days = slots.map((slot) => slot.day);
  const missing = slots.map((slot) => slot.point === null);
  const labels = resolveLabels(SERIES_LABELS, options);
  const legendNames: Record<string, string> = {
    [TYPE_LABELS[0]]: labels.theft,
    [TYPE_LABELS[1]]: labels.leeching,
    "New accounts": labels.newAccounts,
  };
  const tooltipNames: Record<string, string> = {
    [TYPE_LABELS[0]]: options.labels?.theft?.trim() || TYPE_LABELS[0],
    [TYPE_LABELS[1]]: options.labels?.leeching?.trim() || TYPE_LABELS[1],
    "New accounts": labels.newAccounts,
  };
  const typeColors = [
    colors.cdnsa?.theftForRestreaming ?? colors.series[0],
    colors.cdnsa?.cdnLeeching ?? colors.series[1],
  ];
  const labelSize = colors.typography?.labelSize ?? 12;
  return {
    animation: options.motion,
    animationDuration: 400,
    animationEasing: "cubicOut",
    backgroundColor: "transparent",
    textStyle: {
      fontFamily: colors.typography?.fontFamily ?? fontBase.fontFamily,
      fontSize: labelSize,
    },
    color: typeColors,
    grid: {
      top: options.showLegend ? 32 : 16,
      right: options.showNewAccounts ? 56 : 20,
      bottom: 8,
      left: 8,
      containLabel: true,
    },
    legend: {
      show: options.showLegend,
      top: 4,
      left: "center",
      icon: CHART_LEGEND_ICONS.rect,
      itemWidth: 12,
      itemHeight: 8,
      itemGap: 16,
      type: "scroll",
      formatter: (name: string) => legendNames[name] ?? name,
      textStyle: { color: colors.label, fontSize: labelSize, ...fontBase },
    },
    tooltip: {
      trigger: "axis",
      appendTo: () => document.body,
      transitionDuration: 0,
      backgroundColor: colors.tooltipBg,
      borderWidth: 0,
      borderRadius: 8,
      padding: [10, 12],
      textStyle: { color: colors.tooltipText, fontSize: labelSize, ...fontBase },
      axisPointer: { type: "shadow", shadowStyle: { color: withAlpha(colors.label, 0.08) } },
      position: (
        point: number[],
        _params: unknown,
        _dom: unknown,
        _rect: unknown,
        size: { contentSize: number[]; viewSize: number[] },
      ) => positionChartTooltip(point, size),
      formatter: (params: unknown) => {
        const entries = (Array.isArray(params) ? params : [params]) as {
          axisValue?: string;
          seriesName?: string;
          marker?: string;
          value?: number | null;
        }[];
        const day = entries[0]?.axisValue;
        if (typeof day !== "string") return "";
        const heading = `<div style="font-weight:600;margin-bottom:6px">${escapeHtml(dayLong.format(toDate(day)))} · UTC</div>`;
        if (missing[days.indexOf(day)]) return `${heading}<div>${NO_DATA_LABEL}</div>`;
        const total = entries.reduce(
          (sum, entry) =>
            sum +
            (TYPE_LABELS.some((label) => label === entry.seriesName) ? (entry.value ?? 0) : 0),
          0,
        );
        const rows = entries
          .map(
            (entry) =>
              `<div style="display:flex;justify-content:space-between;gap:16px">` +
              `<span>${entry.marker ?? ""}${escapeHtml(tooltipNames[entry.seriesName ?? ""] ?? entry.seriesName ?? "")}</span>` +
              `<b style="font-variant-numeric:tabular-nums">${entry.value == null ? "Unavailable" : number.format(entry.value)}</b></div>`,
          )
          .join("");
        return (
          heading +
          rows +
          `<div style="display:flex;justify-content:space-between;gap:16px;margin-top:6px;padding-top:6px;border-top:1px solid ${withAlpha(colors.tooltipText, 0.18)}">` +
          `<span>Total detections</span><b style="font-variant-numeric:tabular-nums">${number.format(total)}</b></div>`
        );
      },
    },
    xAxis: {
      type: "category",
      data: days,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.axisLine } },
      axisLabel: {
        color: colors.label,
        fontSize: labelSize,
        hideOverlap: true,
        margin: 10,
        formatter: (day: string) => dayLabel.format(toDate(day)),
      },
    },
    yAxis: [
      {
        type: "value",
        minInterval: 1,
        splitNumber: 4,
        axisLabel: { color: colors.label, fontSize: labelSize },
        splitLine: { lineStyle: { color: colors.grid } },
      },
      ...(options.showNewAccounts
        ? [
            {
              type: "value",
              position: "right",
              minInterval: 1,
              splitNumber: 4,
              axisLabel: { color: colors.accent, fontSize: labelSize },
              axisLine: { lineStyle: { color: colors.accent } },
              axisTick: { lineStyle: { color: colors.accent } },
              splitLine: { show: false },
            },
          ]
        : []),
    ],
    series: [
      ...TYPES.map((type, index) => ({
        type: "bar",
        stack: "observed",
        name: TYPE_LABELS[index],
        barMaxWidth: 28,
        barCategoryGap: "38%",
        // Only the top of the stack is rounded. The same card-colour hairline on every type keeps
        // their widths equal and separates them, like the gap between CDN association segments.
        itemStyle: {
          color: typeColors[index],
          borderRadius: index === TYPES.length - 1 ? [4, 4, 0, 0] : 0,
          borderColor: colors.tooltipBg,
          borderWidth: 1,
        },
        emphasis: { focus: "series" },
        data: slots.map((slot) => slot.point?.[type] ?? null),
        ...(index === 0
          ? { markArea: { silent: true, data: categoryNoDataAreas(days, missing, colors) } }
          : {}),
      })),
      ...(options.showNewAccounts
        ? [
            {
              type: "line",
              name: "New accounts",
              yAxisIndex: 1,
              symbol: "diamond",
              symbolSize: 7,
              showAllSymbol: true,
              connectNulls: false,
              smooth: false,
              lineStyle: { color: colors.accent, width: 2 },
              itemStyle: { color: colors.accent },
              emphasis: { focus: "series" },
              data: slots.map((slot) => slot.point?.new_accounts ?? null),
            },
          ]
        : []),
    ],
  };
}

function Summary({ summary }: { summary: DailySummary }) {
  const { latest, peak, dailyAverage } = summary;
  const short = (day: string) => dayLabel.format(toDate(day));
  return (
    <dl className="grid shrink-0 grid-cols-1 gap-x-6 gap-y-3 px-(--density-widget-body-padding) pt-2 pb-5 @sm/widget:grid-cols-3">
      <MetricStat
        size="standard"
        label="Latest day"
        value={latest && latest.total !== 0 ? number.format(latest.total) : "—"}
        detail={latest ? short(latest.day) : undefined}
      />
      <MetricStat
        size="standard"
        label="Daily average"
        value={dailyAverage === null ? "—" : average.format(dailyAverage)}
        detail="per measured UTC day"
      />
      <MetricStat
        size="standard"
        label="Peak day"
        value={peak ? number.format(peak.total) : "—"}
        detail={peak ? short(peak.day) : undefined}
      />
    </dl>
  );
}

function CdnsaDailyTrend({ data, config }: WidgetProps<CdnsaDailyTrendConfig>) {
  const labels = resolveLabels(SERIES_LABELS, config);
  const hostRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts | null>(null);
  const rendered = useRef(false);
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen } = useContext(WidgetFullscreenContext);
  // Entry animation only when the author asks for it ("Animate chart").
  const motion = useChartAnimation(config);
  const series = unwrap<CdnsaDailyTrendData>(data.series);
  const error = firstError(data, ["series"]);
  const loading = data.series?.isLoading ?? false;
  const empty = !loading && !error && !series?.points.length;
  const showSummary = (config.summary ?? defaultConfig.summary) !== "hidden";
  const missingData = missingDataMode(config.missingData);
  // The summary reads measured days only, never the zeros of days not measured yet.
  const summary = useMemo(
    () =>
      series
        ? summarizeDaily(
            dailySlots(series, "gap").flatMap((slot) => (slot.point ? [slot.point] : [])),
          )
        : null,
    [series],
  );

  const draw = (host: HTMLElement, chart: echarts.ECharts) => {
    if (!series || error || empty) return;
    chart.setOption(
      buildDailyOption(series, resolveChartColors(host), {
        showLegend: config.showLegend !== false,
        showNewAccounts: config.showNewAccounts === true,
        labels: config.labels,
        motion,
        missingData,
      }) as echarts.EChartsCoreOption,
      true,
    );
  };

  useEffect(() => {
    const host = hostRef.current;
    if (!host || loading || error || empty || !series) return;
    chartRef.current ??= echarts.init(host, null, { renderer: "canvas" });
    draw(host, chartRef.current);
    rendered.current = true;
    markContentReady();
  }, [
    series,
    loading,
    error,
    empty,
    config.showLegend,
    config.showNewAccounts,
    config.labels,
    missingData,
    motion,
    markContentReady,
  ]);
  useChartLifecycle(hostRef, chartRef, draw);

  const ready = !error && !empty && Boolean(series);

  return (
    <Widget
      title={config.title?.trim() || defaultConfig.title}
      intrinsicHeight={!fullscreen}
      actions={loading && rendered.current ? <UpdatingPill /> : undefined}
    >
      <div className={`flex w-full flex-col ${fullscreen ? "h-full min-h-0" : "flex-1"}`}>
        {ready && showSummary && summary ? <Summary summary={summary} /> : null}
        <div className="relative min-h-(--widget-chart-min-height) w-full flex-1">
          <div
            ref={hostRef}
            role="img"
            aria-label={
              summary?.latest
                ? `Daily distinct observed detections in UTC; latest day ${number.format(summary.latest.total)}, peak ${number.format(summary.peak?.total ?? 0)}${config.showNewAccounts ? `; ${labels.newAccounts}: accounts observed on this day but not the previous UTC day` : ""}`
                : "Daily distinct observed detections in UTC"
            }
            className="absolute inset-0 h-full w-full transition-opacity duration-200"
            style={{ opacity: ready ? 1 : 0 }}
          />
          {error && <WidgetIssueState error={error} className="absolute inset-0" />}
          {empty && (
            <WidgetState
              title="No observed detections"
              description="No observations match the selected period and filters."
              className="absolute inset-0"
            />
          )}
          {loading && !rendered.current && (
            <WidgetSkeleton shape="chart" className="absolute inset-0" />
          )}
        </div>
      </div>
    </Widget>
  );
}

export const Component = CdnsaDailyTrend;
const widgetModule: WidgetModule<CdnsaDailyTrendConfig> = {
  manifest,
  Component,
  defaultConfig,
  getDataRequirements: (config) => ({
    series: {
      mode: "select",
      fields: ["points", "freshness", ...(config.showNewAccounts ? ["new_accounts"] : [])],
    },
  }),
};
export default widgetModule;
